import { defineStore } from 'pinia';
import { useTireContextStore } from '~/stores/tireContext';
import type { TireLookupByRegistrationNumberResponse, TireLookupTireType } from '~/types/generated-types';
import { Season } from '~/types/generated-types';

interface TireContextState {
  _frontTireSize: TireLookupTireType | undefined;
  _backTireSize: TireLookupTireType | undefined;
  _transportVehicle: boolean;
  _currentSeason: Season | undefined;
  _hasManualTireSize: boolean;
  _hasCarButChangedDimensions: boolean;
  _hasTransportVehicleButSetNoTransport: {
    vehicle: string;
    setNoTransport: boolean;
  };
}

const initialState: () => TireContextState = () => {
  return {
    _frontTireSize: undefined,
    _backTireSize: undefined,
    _transportVehicle: false,
    _currentSeason: undefined,
    _hasManualTireSize: false,
    _hasCarButChangedDimensions: false,
    _hasTransportVehicleButSetNoTransport: {
      vehicle: '',
      setNoTransport: false,
    },
  };
};

export const useTireSizeContextStore = defineStore('tireSizeContext', {
  state: initialState,
  persist: {
    storage: persistedState.sessionStorage,
  },
  getters: {
    frontTireSize: (state: TireContextState): TireLookupTireType | undefined => state._frontTireSize,
    backTireSize: (state: TireContextState): TireLookupTireType | undefined => state._backTireSize,
    frontTireDisplay: (state: TireContextState): string | undefined => {
      if (!state._frontTireSize) return undefined;
      return state._frontTireSize.displayValue;
    },
    backTireDisplay: (state: TireContextState): string | undefined => {
      if (!state._backTireSize) return undefined;
      return state._backTireSize.displayValue;
    },
    areDimensionsSame: (state: TireContextState): boolean => {
      if (!state._backTireSize || !state._backTireSize) return false;
      return state._frontTireSize?.displayValue === state._backTireSize?.displayValue;
    },
    hasDimensions: (state: TireContextState): boolean => !!state._backTireSize && !!state._frontTireSize,
    tireSeason: (state: TireContextState): Season => (state._currentSeason ? state._currentSeason : Season.Unknown),
    hasManualTireSize: (state: TireContextState): boolean => state._hasManualTireSize,
    hasTransportVehicleButSetNoTransport: (state: TireContextState): { vehicle: string; setNoTransport: boolean } =>
      state._hasTransportVehicleButSetNoTransport,
  },
  actions: {
    setFrontTireSize(size: TireLookupTireType | undefined, manualDimension: boolean, recommendedDimension: boolean = false): void {
      this._hasCarButChangedDimensions = recommendedDimension;

      if (!size) return;

      this._frontTireSize = {
        ...(size as TireLookupTireType),
        displayValue: manualDimension ? `${size?.width}/${size?.profile}${size?.construction}${size?.diameter}` : size?.displayValue ?? '',
      };
      this._hasManualTireSize = manualDimension;
    },
    setBackTireSize(size: TireLookupTireType | undefined, manualDimension: boolean, recommendedDimension: boolean = false): void {
      this._hasCarButChangedDimensions = recommendedDimension;

      if (!size) return;

      this._backTireSize = {
        ...(size as TireLookupTireType),
        displayValue: manualDimension ? `${size?.width}/${size?.profile}${size?.construction}${size?.diameter}` : size?.displayValue ?? '',
      };
      this._hasManualTireSize = manualDimension;
    },
    changeTireSizes(tireInformation?: TireLookupByRegistrationNumberResponse | undefined): void {
      const tireSet = tireInformation?.tires?.tireSets?.find((tireSet) => tireSet?.season.toUpperCase() === this._currentSeason);
      if (tireSet) {
        this._frontTireSize = tireSet?.front;
        this._backTireSize = tireSet?.rear;
      } else {
        this._frontTireSize = tireInformation?.tires?.tireSets?.at(0)?.front;
        this._backTireSize = tireInformation?.tires?.tireSets?.at(0)?.rear;
      }
      this._hasManualTireSize = false;
      this._hasCarButChangedDimensions = false;
    },
    changeTireSeason(season: Season): void {
      this._currentSeason = season;

      const tireSizeStore = useTireContextStore();

      if (tireSizeStore.tireInformation && !this._hasManualTireSize && !this._hasCarButChangedDimensions) {
        const tireSet = tireSizeStore.tireInformation?.tires?.tireSets?.find(
          (tireSet) => tireSet?.season.toUpperCase() === this._currentSeason
        );
        if (tireSet) {
          this._frontTireSize = tireSet?.front;
          this._backTireSize = tireSet?.rear;
        } else {
          this._frontTireSize = tireSizeStore.tireInformation?.tires?.tireSets?.at(0)?.front;
          this._backTireSize = tireSizeStore.tireInformation?.tires?.tireSets?.at(0)?.rear;
        }
      }
    },
    setTransportVehicle(isTransportVehicle: boolean): void {
      this._transportVehicle = isTransportVehicle;
    },
    setHasTransportVehicleButSetNoTransport(hasTransportVehicleButSetNoTransport: { vehicle: string; setNoTransport: boolean }): void {
      this._hasTransportVehicleButSetNoTransport = hasTransportVehicleButSetNoTransport;
    },
  },
});
